import {Button, makeStyles} from '@material-ui/core'
import React, {useEffect, useMemo, useState} from 'react'
import ProductRegistrationOrganizationListStyle from 'pages/ProductRegistrationOrganization/ProductRegistrationOrganizationList/style'

import {
  OFFER_TYPE,
  OffersType,
  ORG_ID_APPOINT,
  PAYMENT_METHOD,
  PAYMENT_METHOD_OPTIONS,
  PaymentMethod,
  PREPAYMENT_ITEM,
  SALE_STATUS,
  TARGET_TYPE,
  UNIT_PRICES,
  USAGE_PERIOD_UNIT,
} from 'pages/ProductsPages/constant'
import {useTranslation} from 'react-i18next'
import useProductManagement from 'features/productManagement/useProductManagement'
import {
  getListOrgRegisteredProductApi,
  GoodsResponse,
  ListProductType,
  OfferItemsType,
  SaleTarget,
} from 'api/productApi'
import useOrganization from 'features/org/useOrganization'
import SectionInstitution from 'pages/ProductRegistrationOrganization/Component/SectionInstitution'
import DetailProductOrderDialog from 'components/Dialog/Product/DetailProductOrderDialog'
import {formatPriceNumber} from 'helpers/uiHelper'
import {usdToCent} from 'helpers/paymentHelper'

type Props = {
  tab: string
}
const useStyles = makeStyles(ProductRegistrationOrganizationListStyle)
const BulkSupply = (props: Props) => {
  const {tab} = props
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const {onSearch, listProductResponse, getOrder} = useProductManagement()
  const [productType, setProductType] = useState<string>('')
  const [selProduct, setSelProduct] = useState<{
    goodsId: string
    name: GoodsResponse['goodsName']
    period: string | number
    sale: number[]
  } | null>(null)
  const [listRegisteredOrg, setListRegisteredOrg] = useState<string[]>([])
  const {organization} = useOrganization()
  const [isOpenDescription, setIsOpenDescription] = useState<boolean>(false)
  const [infoProduct, setInfoProduct] = useState<{
    goodsId: string
    goodsName: string
    description: string
    paymentMethod: string
    amountProduct: string
    originalAmountProduct: string
    Duration: number
    canCharge: boolean
    offerType: OffersType
    offerItems: (OfferItemsType & {
      usageAmount: number
      chargeAmount: number
      order: number
    })[]
    saleTarget: SaleTarget
  }>({
    goodsId: '',
    goodsName: '',
    description: '',
    paymentMethod: '',
    amountProduct: '',
    originalAmountProduct: '',
    Duration: 0,
    canCharge: false,
    offerType: '',
    offerItems: [],
    saleTarget: {
      targetOp: '',
      targets: [],
    },
  })

  const displayPaymentMethod = (type: PaymentMethod) => {
    let paymentMethod: PaymentMethod = PAYMENT_METHOD.PREPAYMENT
    PAYMENT_METHOD_OPTIONS.forEach((item) => {
      if (item.key === type) {
        paymentMethod = t(`${item.label}`)
      }
    })
    return paymentMethod
  }
  const handleClick = (productType: ListProductType) => {
    setProductType(productType.goods.goodsId || '')
    const sale = Array.from(
      new Set(
        productType.sale.saleTarget.targets
          .filter(
            (item: {targetType: string; targetId: string}) =>
              item.targetType === TARGET_TYPE.ORG_ID &&
              item.targetId !== ORG_ID_APPOINT,
          )
          .map((item: {targetId: string}) => Number(item.targetId)),
      ),
    )
    setSelProduct({
      goodsId: productType.goods.goodsId || '',
      name: productType.goods.goodsName || '',
      period:
        productType.offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME
          ? t('IUnlimited')
          : `${productType.offer.usagePeriod || ''} ${t('IMonths')}`,
      sale,
    })
  }

  const goodsName = (data: GoodsResponse['goodsName']) =>
    data.find(
      (item) => item.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
    )?.name || ''

  const goodsDesc = (data: GoodsResponse['goodsName']) =>
    data.find(
      (item) => item.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
    )?.description || ''

  const extractProductInfo = (data: ListProductType) => {
    let goodsName = ''
    let description = ''

    const lang = i18n.language

    data.goods.goodsName.forEach((item) => {
      if (item.langCode === lang) {
        goodsName = item.name
        description = item.description
      }
    })

    const targetCurrency =
      organization?.country.code === 'KR' ? UNIT_PRICES.KRW : UNIT_PRICES.USD

    const salePrice =
      data.sale.salePrice.find(
        (price: {currency: string; price: number}) =>
          price.currency === targetCurrency,
      )?.price ?? 0

    const originalPrice =
      data.sale.price.find(
        (price: {currency: string; price: number}) =>
          price.currency === targetCurrency,
      )?.price ?? 0

    const amountProduct =
      targetCurrency === UNIT_PRICES.USD ? usdToCent(salePrice) : salePrice

    const originalAmountProduct =
      targetCurrency === UNIT_PRICES.USD
        ? usdToCent(originalPrice)
        : originalPrice

    return {
      goodsId: data.goods.goodsId,
      goodsName,
      description,
      paymentMethod: data.sale.paymentMethod,
      amountProduct: `${targetCurrency} ${formatPriceNumber(amountProduct)}`,
      originalAmountProduct: `${targetCurrency} ${formatPriceNumber(
        originalAmountProduct,
      )}`,
      Duration: data.offer.usagePeriod,
      offerType: data.offer.offerType,
      canCharge: false,
      offerItems: data.offer.offerItems.map((item) => ({
        ...item,
        chargeAmount: 0,
        usageAmount: 1,
        order: getOrder(item.pkgItemId),
      })),
      saleTarget: data.sale.saleTarget,
    }
  }

  const onViewDescription = (data: ListProductType) => {
    const info = extractProductInfo(data)
    setInfoProduct(info)
    setIsOpenDescription(true)
  }

  const displayPaymentMethodDescription = useMemo(() => {
    if (infoProduct.paymentMethod === PAYMENT_METHOD.PREPAYMENT) {
      if (infoProduct.offerType === PREPAYMENT_ITEM.PROVIDER) {
        return t('ISubscription')
      }
      if (infoProduct.offerType === PREPAYMENT_ITEM.MONTHLY_OFFER) {
        return t('IPrepaid')
      }
    }
    return t('IManualPaid')
  }, [infoProduct.paymentMethod, t])

  const getListOrgRegisteredProduct = async (goodsId: string) => {
    const res = await getListOrgRegisteredProductApi(goodsId)
    setListRegisteredOrg(res.data.orgIds)
  }

  useEffect(() => {
    setSelProduct(null)
  }, [listProductResponse])

  useEffect(() => {
    if (selProduct) {
      getListOrgRegisteredProduct(selProduct.goodsId)
    }
  }, [selProduct])

  useEffect(() => {
    onSearch({
      offerType: tab,
      paymentMethod: PAYMENT_METHOD.PREPAYMENT,
      saleStatus: SALE_STATUS.ON_SALE,
      targets: `${TARGET_TYPE.ORG_ID},${ORG_ID_APPOINT}`,
    })
  }, [tab])
  return (
    <div>
      <div className={classes.productsGrid}>
        {listProductResponse?.map((item) => (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <div
            key={item.goods.goodsId}
            className={`${classes.infoProduct} ${
              item.goods.goodsId === productType ? classes.productActive : ''
            }`}
            onClick={() => handleClick(item)}
          >
            <div className={classes.productTitle}>
              {t('IProductNameV1')}: {goodsName(item.goods.goodsName)}
            </div>
            <div className={classes.productType}>
              {t('IType')}: {displayPaymentMethod(item.sale.paymentMethod)}
            </div>
            <div>
              {t('IPeriod')}:{' '}
              {item.offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME
                ? t('IUnlimited')
                : `${item.offer.usagePeriod || ''} ${t('IMonths')}`}
            </div>
            <div>
              {t('IDescription')}: {goodsDesc(item.goods.goodsName)}
            </div>
            <div className={classes.productBtnV1}>
              <Button
                color='primary'
                variant='contained'
                onClick={(e) => {
                  e.stopPropagation()
                  onViewDescription(item)
                }}
              >
                {t('IDetailDescription')}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <SectionInstitution
        goodsId={selProduct?.goodsId || ''}
        name={goodsName(selProduct?.name || [])}
        period={selProduct?.period || ''}
        listOrgRegistered={listRegisteredOrg}
      />
      <DetailProductOrderDialog
        isOpen={isOpenDescription}
        onClose={() => setIsOpenDescription(false)}
        typeCharge={infoProduct?.paymentMethod as PaymentMethod}
        totalAmount={infoProduct?.amountProduct}
        totalOriginalAmount={infoProduct?.originalAmountProduct}
        offerType={infoProduct?.offerType}
        productName={infoProduct?.goodsName}
        paymentMethod={displayPaymentMethodDescription}
        duration={infoProduct.Duration}
        description={infoProduct.description}
        goods={infoProduct.offerItems}
      />
    </div>
  )
}
export default BulkSupply
